import React, { useState, useEffect } from 'react';

/**
 * NumberInput - Numeric input field
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {number} props.value - Current numeric value
 * @param {Function} props.onChange - Change handler
 * @param {number} props.min - Minimum value (from feature.property.minValue)
 * @param {number} props.max - Maximum value (from feature.property.maxValue)
 * @param {number} props.step - Step increment (from feature.property.step)
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.className - Additional CSS classes
 */
const NumberInput = ({ 
    feature, 
    value,
    onChange,
    min,
    max,
    step = 1,
    placeholder = 'Enter number...',
    disabled = false, 
    className = ''
}) => {
    const [error, setError] = useState('');
    const [touched, setTouched] = useState(false);
    const displayText = feature?.property?.displayText || feature?.name || 'Number Input';
    
    // Calculate effective min/max from props or feature
    const effectiveMin = min ?? feature?.property?.minValue ?? Number.MIN_SAFE_INTEGER;
    const effectiveMax = max ?? feature?.property?.maxValue ?? Number.MAX_SAFE_INTEGER;
    const effectiveStep = step ?? feature?.property?.step ?? 1;
    
    const handleChange = (e) => {
        const numericValue = parseFloat(e.target.value);
        if (!isNaN(numericValue)) {
            onChange({ target: { value: numericValue } });
            setError('');
        } else if (e.target.value === '') {
            onChange({ target: { value: '' } });
        }
    };
    
    const handleBlur = (e) => {
        setTouched(true);
        const numericValue = parseFloat(e.target.value);
        
        if (isNaN(numericValue)) {
            setError('Please enter a valid number');
            return;
        }
        
        if (numericValue < effectiveMin) {
            setError(`Value must be at least ${effectiveMin}`);
        } else if (numericValue > effectiveMax) {
            setError(`Value must be at most ${effectiveMax}`);
        } else {
            setError('');
        }
    };
    
    // Clamp displayed value to min/max
    const displayValue = value === null || value === undefined || isNaN(value) 
        ? '' 
        : Math.min(Math.max(effectiveMin, value), effectiveMax);
    
    return (
        <div 
            className="number-input-wrapper"
            style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
        >
            <label style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>{displayText}</span>
                {feature?.property?.isOptional !== false && (
                    <span style={{ color: 'var(--error)', fontSize: '0.75rem' }}>*</span>
                )}
            </label>
            <input
                className={className}
                type="number"
                value={displayValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={placeholder}
                min={effectiveMin}
                max={effectiveMax}
                step={effectiveStep}
                disabled={disabled}
                style={{
                    border: `1px solid ${error ? 'var(--error)' : 'var(--border)'}`,
                    outline: 'none',
                    cursor: disabled ? 'not-allowed' : 'text',
                    opacity: disabled ? 0.6 : 1
                }}
                disabled={disabled}
            />
            {error && touched && (
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--error)',
                    minHeight: '1rem'
                }}>
                    {error}
                </span>
            )}
            {(effectiveMin !== Number.MIN_SAFE_INTEGER || effectiveMax !== Number.MAX_SAFE_INTEGER) && !error && (
                <span style={{
                    fontSize: '0.7rem',
                    color: 'var(--text-secondary)',
                    fontStyle: 'italic'
                }}>
                    Range: {effectiveMin} to {effectiveMax}
                </span>
            )}
            {feature?.property?.description && !error && (
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    fontStyle: 'italic'
                }}>
                    {feature.property.description}
                </span>
            )}
        </div>
    );
};

export default NumberInput;
